import React, { useState, useEffect, useCallback } from 'react';
import { X, Key, Shield, Activity, Cpu, Loader2, RefreshCw, Trash2, UserX, UserCheck, Mail, AlertTriangle, Link2, Plus, Building2 } from 'lucide-react';
import { API } from '../../App';
import { useToast } from '../ui/Toast';

const card = 'bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-3';
const btnPrimary = 'px-3 py-1.5 min-h-[36px] bg-[#FF4D00] text-[#0A0A0A] font-bold text-xs rounded hover:bg-[#E63E00] transition-colors disabled:opacity-50';
const btnDanger = 'px-3 py-1.5 min-h-[36px] bg-[#A61E17] text-white text-xs rounded hover:bg-[#8B1912] transition-colors disabled:opacity-50';
const btnSecondary = 'px-3 py-1.5 min-h-[36px] border border-[#2A2A2A] text-[#A0A0A0] text-xs rounded hover:border-[#FF4D00] hover:text-[#F5F1E8] transition-colors disabled:opacity-50';

const ROLE_COLORS = { owner: '#FF4D00', admin: '#F2A900', user: '#00D4FF', account_super_admin: '#FF4D00' };
const STATUS_COLORS = { accepted: '#22C55E', pending: '#F2A900', invited: '#00D4FF', disabled: '#A61E17' };

export default function UserDetailFlyout({ workspaceId, userId, onClose, onRefresh, actorRole }) {
  const { toast } = useToast();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // 'revoke' | 'regenerate' | 'remove' | 'disable'

  const canManage = actorRole === 'owner' || actorRole === 'account_super_admin';
  const canAdminister = canManage || actorRole === 'admin' || actorRole === 'account_admin';

  const fetchDetails = useCallback(async () => {
    try {
      const r = await API.get(`/api/workspaces/${workspaceId}/members/${userId}/details`);
      setDetails(r.data);
    } catch (e) {
      console.error('Failed to fetch member details:', e);
    }
    setLoading(false);
  }, [workspaceId, userId]);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  const handleChangeRole = async (newRole) => {
    setActionLoading('role');
    try {
      await API.put(`/api/workspaces/${workspaceId}/members/${userId}/role`, { role: newRole });
      await fetchDetails();
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to change role'); }
    setActionLoading(null);
  };

  const handleToggleDisable = async () => {
    const disabling = details.user.status !== 'disabled';
    setActionLoading('disable');
    setConfirmAction(null);
    try {
      await API.put(`/api/workspaces/${workspaceId}/members/${userId}/disable`, { disabled: disabling });
      await fetchDetails();
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setActionLoading(null);
  };

  const handleRemove = async () => {
    setActionLoading('remove');
    setConfirmAction(null);
    try {
      await API.delete(`/api/workspaces/${workspaceId}/members/${userId}`);
      onRefresh();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove user'); }
    setActionLoading(null);
  };

  const handleRevokeKey = async () => {
    setActionLoading('revoke');
    setConfirmAction(null);
    try {
      await API.delete(`/api/workspaces/${workspaceId}/members/${userId}/key`);
      await fetchDetails();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to revoke key'); }
    setActionLoading(null);
  };

  const handleRegenerateKey = async () => {
    setActionLoading('regenerate');
    setConfirmAction(null);
    try {
      await API.post(`/api/workspaces/${workspaceId}/members/${userId}/key/regenerate`);
      await fetchDetails();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to regenerate key'); }
    setActionLoading(null);
  };

  const handleResendInvite = async () => {
    setActionLoading('resend');
    try {
      const r = await API.post(`/api/workspaces/${workspaceId}/invites/${userId}/resend`);
      toast.success(`Invite resent to ${r.data?.email || 'user'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Resend failed'); }
    setActionLoading(null);
  };

  // OpenRouter key reveal (#645) — plaintext lazy-loaded on explicit click
  const [revealedOrKey, setRevealedOrKey] = useState(null);
  const [revealOrLoading, setRevealOrLoading] = useState(false);
  const [revealOrError, setRevealOrError] = useState(null);
  const [orKeyCopied, setOrKeyCopied] = useState(false);

  // Clear reveal state when user changes
  useEffect(() => {
    setRevealedOrKey(null);
    setRevealOrError(null);
    setOrKeyCopied(false);
  }, [userId]);

  const revealOpenRouterKey = async () => {
    setRevealOrLoading(true);
    setRevealOrError(null);
    try {
      const r = await API.get(`/api/admin/users/${userId}/openrouter-key`);
      setRevealedOrKey(r.data.key);
    } catch (e) {
      setRevealOrError(e.response?.status === 404
        ? 'No key available or not authorized.'
        : (e.response?.data?.detail || 'Failed to reveal key'));
    }
    setRevealOrLoading(false);
  };

  const copyOrKey = () => {
    if (!revealedOrKey) return;
    navigator.clipboard.writeText(revealedOrKey);
    setOrKeyCopied(true);
    setTimeout(() => setOrKeyCopied(false), 2000);
  };

  // MCP token handlers (#515)
  const [mcpNewKey, setMcpNewKey] = useState(null);

  const handleGenerateMcpToken = async () => {
    setActionLoading('mcp_generate');
    setConfirmAction(null);
    try {
      const r = await API.post(`/api/workspaces/${workspaceId}/members/${userId}/mcp-token`, {});
      setMcpNewKey(r.data.key);
      await fetchDetails();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate MCP token'); }
    setActionLoading(null);
  };

  const handleRegenerateMcpToken = async () => {
    setActionLoading('mcp_regenerate');
    setConfirmAction(null);
    try {
      const r = await API.post(`/api/workspaces/${workspaceId}/members/${userId}/mcp-token/regenerate`);
      setMcpNewKey(r.data.key);
      await fetchDetails();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to regenerate MCP token'); }
    setActionLoading(null);
  };

  const handleRevokeMcpToken = async () => {
    setActionLoading('mcp_revoke');
    setConfirmAction(null);
    try {
      await API.delete(`/api/workspaces/${workspaceId}/members/${userId}/mcp-token`);
      setMcpNewKey(null);
      await fetchDetails();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to revoke MCP token'); }
    setActionLoading(null);
  };

  // Department assignment (#534)
  const [departments, setDepartments] = useState([]);
  const [userDept, setUserDept] = useState(null);
  const [selectedDeptId, setSelectedDeptId] = useState('');
  const [deptRole, setDeptRole] = useState('member');

  useEffect(() => {
    if (!workspaceId) return;
    API.get('/api/enterprise/departments', { params: { workspace_id: workspaceId } })
      .then(r => setDepartments(r.data?.departments || []))
      .catch(() => {});
  }, [workspaceId]);

  useEffect(() => {
    if (!departments.length || !userId) return;
    // Check each department for this user
    Promise.all(departments.map(d =>
      API.get(`/api/enterprise/departments/${d.id}/members`).then(r => {
        const found = (r.data?.members || []).find(m => m.user_id === userId);
        return found ? { ...found, department_id: d.id, department_name: d.name } : null;
      }).catch(() => null)
    )).then(results => {
      const match = results.find(r => r);
      setUserDept(match || null);
      if (match) { setSelectedDeptId(match.department_id); setDeptRole(match.role || 'member'); }
    });
  }, [departments, userId]);

  const handleDeptSave = async () => {
    setActionLoading('dept');
    try {
      // Remove from current department if exists
      if (userDept) {
        await API.delete(`/api/enterprise/departments/${userDept.department_id}/members/${userId}`);
      }
      // Add to new department if selected
      if (selectedDeptId) {
        await API.post(`/api/enterprise/departments/${selectedDeptId}/members`, { user_id: userId, role: deptRole });
        const dept = departments.find(d => d.id === selectedDeptId);
        setUserDept({ department_id: selectedDeptId, department_name: dept?.name, role: deptRole, user_id: userId });
      } else {
        setUserDept(null);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update department'); }
    setActionLoading(null);
  };

  const isOwner = details?.user?.role === 'owner' || details?.user?.role === 'account_super_admin';

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />

      {/* Flyout panel */}
      <div className="fixed right-0 top-0 h-full w-[440px] max-w-[90vw] bg-[#0F0F0F] border-l border-[#2A2A2A] z-50 overflow-y-auto shadow-2xl animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 bg-[#0F0F0F] border-b border-[#2A2A2A] px-4 py-3 flex items-center justify-between z-10">
          <h3 className="text-sm font-bold font-['Barlow_Condensed'] text-[#F5F1E8] uppercase tracking-wider">
            User Details
          </h3>
          <button onClick={onClose} className="text-[#A0A0A0] hover:text-[#F5F1E8] p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[#FF4D00]" />
          </div>
        ) : !details ? (
          <div className="text-center py-20 text-[#A0A0A0] text-sm">Failed to load user details</div>
        ) : (
          <div className="p-4 space-y-4">
            {/* User Identity */}
            <div className={card}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#2A2A2A] flex items-center justify-center text-sm font-bold text-[#F5F1E8]">
                  {(details.user.name || details.user.email || '?')[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-[#F5F1E8] font-mono truncate">
                    {details.user.name || details.user.email}
                  </div>
                  <div className="text-[10px] text-[#A0A0A0] font-mono truncate">{details.user.email}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div>
                  <span className="text-[#666]">Role</span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="px-1.5 py-0.5 rounded font-mono" style={{
                      color: ROLE_COLORS[details.user.role] || '#A0A0A0',
                      backgroundColor: `${ROLE_COLORS[details.user.role] || '#A0A0A0'}20`,
                    }}>{(details.user.role || 'user').replace('account_', '').toUpperCase()}</span>
                    {canManage && !isOwner && (
                      <select
                        value={details.user.role}
                        onChange={e => handleChangeRole(e.target.value)}
                        disabled={actionLoading === 'role'}
                        className="bg-[#0F0F0F] border border-[#2A2A2A] text-[10px] font-mono text-[#A0A0A0] px-1 py-0.5 rounded"
                      >
                        <option value="user">user</option>
                        <option value="admin">admin</option>
                        <option value="owner">owner</option>
                      </select>
                    )}
                    {actionLoading === 'role' && <Loader2 className="w-3 h-3 animate-spin text-[#A0A0A0]" />}
                  </div>
                </div>
                <div>
                  <span className="text-[#666]">Status</span>
                  <div className="mt-0.5">
                    <span className="px-1.5 py-0.5 rounded font-mono" style={{
                      color: STATUS_COLORS[details.user.status] || '#A0A0A0',
                      backgroundColor: `${STATUS_COLORS[details.user.status] || '#A0A0A0'}20`,
                    }}>{(details.user.status || 'accepted').toUpperCase()}</span>
                  </div>
                </div>
                <div>
                  <span className="text-[#666]">Joined</span>
                  <div className="text-[#A0A0A0] font-mono mt-0.5">
                    {details.user.joined_at ? new Date(details.user.joined_at).toLocaleDateString() : '—'}
                  </div>
                </div>
                <div>
                  <span className="text-[#666]">Account Created</span>
                  <div className="text-[#A0A0A0] font-mono mt-0.5">
                    {details.user.created_at ? new Date(details.user.created_at).toLocaleDateString() : '—'}
                  </div>
                </div>
              </div>
            </div>

            {/* Account Actions */}
            {canAdminister && !isOwner && (
              <div className={card}>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-3.5 h-3.5 text-[#FF4D00]" />
                  <span className="text-xs font-mono text-[#F5F1E8]">Actions</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {details.user.status === 'invited' && (
                    <button onClick={handleResendInvite} disabled={actionLoading === 'resend'} className={btnSecondary}>
                      {actionLoading === 'resend' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Mail className="w-3 h-3 inline mr-1" />Resend Invite</>}
                    </button>
                  )}
                  {confirmAction === 'disable' ? (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-[#F2A900] font-mono">
                        {details.user.status === 'disabled' ? 'Enable?' : 'Disable?'}
                      </span>
                      <button onClick={handleToggleDisable} className={btnDanger + ' !text-[10px]'}>Yes</button>
                      <button onClick={() => setConfirmAction(null)} className={btnSecondary + ' !text-[10px]'}>No</button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmAction('disable')} disabled={actionLoading === 'disable'} className={btnSecondary}>
                      {actionLoading === 'disable' ? <Loader2 className="w-3 h-3 animate-spin" /> :
                        details.user.status === 'disabled' ? <><UserCheck className="w-3 h-3 inline mr-1" />Enable</> : <><UserX className="w-3 h-3 inline mr-1" />Disable</>}
                    </button>
                  )}
                  {confirmAction === 'remove' ? (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-[#A61E17] font-mono">Remove from workspace?</span>
                      <button onClick={handleRemove} className={btnDanger + ' !text-[10px]'}>Yes</button>
                      <button onClick={() => setConfirmAction(null)} className={btnSecondary + ' !text-[10px]'}>No</button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmAction('remove')} disabled={actionLoading === 'remove'} className={btnDanger}>
                      {actionLoading === 'remove' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Trash2 className="w-3 h-3 inline mr-1" />Remove</>}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Department Assignment (#534) */}
            {canAdminister && departments.length > 0 && (
              <div className={card}>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-3.5 h-3.5 text-[#FF4D00]" />
                  <span className="text-xs font-mono text-[#F5F1E8]">Department</span>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedDeptId}
                    onChange={e => setSelectedDeptId(e.target.value)}
                    className="flex-1 bg-[#0F0F0F] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none"
                  >
                    <option value="">No department</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                  <select
                    value={deptRole}
                    onChange={e => setDeptRole(e.target.value)}
                    className="bg-[#0F0F0F] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none"
                  >
                    <option value="member">Member</option>
                    <option value="lead">Lead</option>
                    <option value="delegated_admin">Admin</option>
                  </select>
                  <button onClick={handleDeptSave} disabled={actionLoading === 'dept'} className={btnPrimary}>
                    {actionLoading === 'dept' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                  </button>
                </div>
                {userDept && (
                  <div className="text-[10px] text-[#A0A0A0] font-mono mt-1.5">
                    Currently: <span className="text-[#00D4FF]">{userDept.department_name}</span> as {userDept.role}
                  </div>
                )}
              </div>
            )}

            {/* OpenRouter Key Section */}
            <div className={card}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Key className="w-3.5 h-3.5 text-[#F2A900]" />
                  <span className="text-xs font-mono text-[#F5F1E8]">OpenRouter API Key</span>
                </div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono ${
                  details.key.status === 'provisioned' ? 'bg-[#22C55E20] text-[#22C55E]' : 'bg-[#A0A0A020] text-[#A0A0A0]'
                }`}>
                  {details.key.status === 'provisioned' ? 'PROVISIONED' : 'NOT PROVISIONED'}
                </span>
              </div>

              {details.key.status === 'provisioned' ? (
                <div className="space-y-2">
                  {/* Key value — show revealed plaintext or masked hash (#645) */}
                  <div className="text-[10px]">
                    <span className="text-[#666]">Key</span>
                    <div className="text-[#A0A0A0] font-mono mt-0.5 break-all">
                      {revealedOrKey || details.key.hash}
                    </div>
                    {revealOrError && <div className="text-[#A61E17] font-mono mt-0.5">{revealOrError}</div>}
                    {revealedOrKey && <div className="text-[#666] font-mono mt-0.5">Reveal action has been audited.</div>}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-[10px]">
                    <div>
                      <span className="text-[#666]">Spend Limit</span>
                      <div className="text-[#00D4FF] font-mono mt-0.5">
                        {details.key.limit != null ? `$${details.key.limit}` : 'Unlimited'}
                        {details.key.limit_reset && <span className="text-[#666]"> / {details.key.limit_reset}</span>}
                      </div>
                    </div>
                    <div>
                      <span className="text-[#666]">Usage This Month</span>
                      <div className="text-[#F5F1E8] font-mono mt-0.5">${(details.key.usage_monthly || 0).toFixed(2)}</div>
                    </div>
                    <div>
                      <span className="text-[#666]">Remaining</span>
                      <div className="text-[#22C55E] font-mono mt-0.5">
                        {details.key.limit_remaining != null ? `$${details.key.limit_remaining.toFixed(2)}` : '—'}
                      </div>
                    </div>
                  </div>

                  {canAdminister && (
                    <div className="flex flex-wrap gap-2 pt-1 border-t border-[#2A2A2A]">
                      {confirmAction === 'revoke' ? (
                        <div className="flex items-center gap-1.5">
                          <AlertTriangle className="w-3 h-3 text-[#A61E17]" />
                          <span className="text-[10px] text-[#A61E17] font-mono">Revoke key permanently?</span>
                          <button onClick={handleRevokeKey} className={btnDanger + ' !text-[10px]'}>Yes</button>
                          <button onClick={() => setConfirmAction(null)} className={btnSecondary + ' !text-[10px]'}>No</button>
                        </div>
                      ) : confirmAction === 'regenerate' ? (
                        <div className="flex items-center gap-1.5">
                          <AlertTriangle className="w-3 h-3 text-[#F2A900]" />
                          <span className="text-[10px] text-[#F2A900] font-mono">Regenerate? Old key will be revoked.</span>
                          <button onClick={handleRegenerateKey} className={btnPrimary + ' !text-[10px]'}>Yes</button>
                          <button onClick={() => setConfirmAction(null)} className={btnSecondary + ' !text-[10px]'}>No</button>
                        </div>
                      ) : (
                        <>
                          {!revealedOrKey ? (
                            <button onClick={revealOpenRouterKey} disabled={revealOrLoading} className={btnSecondary}>
                              {revealOrLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Key className="w-3 h-3 inline mr-1" />Reveal</>}
                            </button>
                          ) : (
                            <>
                              <button onClick={copyOrKey} className={btnSecondary}>
                                {orKeyCopied ? 'Copied!' : 'Copy'}
                              </button>
                              <button onClick={() => { setRevealedOrKey(null); setOrKeyCopied(false); }} className={btnSecondary}>
                                Hide
                              </button>
                            </>
                          )}
                          <button onClick={() => setConfirmAction('regenerate')} disabled={actionLoading === 'regenerate'} className={btnSecondary}>
                            {actionLoading === 'regenerate' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RefreshCw className="w-3 h-3 inline mr-1" />Regenerate</>}
                          </button>
                          <button onClick={() => setConfirmAction('revoke')} disabled={actionLoading === 'revoke'} className={btnDanger}>
                            {actionLoading === 'revoke' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Trash2 className="w-3 h-3 inline mr-1" />Revoke</>}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-[10px] text-[#666] font-mono">
                    No API key provisioned for this user.
                  </p>
                  {canAdminister && (
                    <button
                      onClick={handleRegenerateKey}
                      disabled={actionLoading === 'regenerate'}
                      className={btnPrimary}
                    >
                      {actionLoading === 'regenerate' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Key className="w-3 h-3 inline mr-1" />Provision Key</>}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Fray MCP Token (#515) */}
            <div className={card}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Link2 className="w-3.5 h-3.5 text-[#00D4FF]" />
                  <span className="text-xs font-mono text-[#F5F1E8]">Fray MCP Token</span>
                </div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono ${
                  details.mcp_token?.status === 'generated' ? 'bg-[#22C55E20] text-[#22C55E]' : 'bg-[#A0A0A020] text-[#A0A0A0]'
                }`}>
                  {details.mcp_token?.status === 'generated' ? 'GENERATED' : 'NOT GENERATED'}
                </span>
              </div>

              {/* Show newly generated key (only visible once) */}
              {mcpNewKey && (
                <div className="mb-2 p-2 bg-[#FF4D0010] border border-[#FF4D00] rounded">
                  <div className="text-[10px] text-[#F2A900] font-mono mb-1">⚠ Save this key — shown once only:</div>
                  <div className="text-[11px] text-[#F5F1E8] font-mono break-all select-all bg-[#0A0A0A] p-1.5 rounded">{mcpNewKey}</div>
                  <button onClick={() => { navigator.clipboard.writeText(mcpNewKey); }} className={btnSecondary + ' mt-1.5 !text-[10px]'}>
                    Copy to clipboard
                  </button>
                </div>
              )}

              {details.mcp_token?.status === 'generated' ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2 text-[10px]">
                    <div>
                      <span className="text-[#666]">Token Prefix</span>
                      <div className="text-[#A0A0A0] font-mono mt-0.5">{details.mcp_token.prefix}...</div>
                    </div>
                    <div>
                      <span className="text-[#666]">Name</span>
                      <div className="text-[#A0A0A0] font-mono mt-0.5">{details.mcp_token.name}</div>
                    </div>
                    <div>
                      <span className="text-[#666]">Created</span>
                      <div className="text-[#A0A0A0] font-mono mt-0.5">
                        {details.mcp_token.created_at ? new Date(details.mcp_token.created_at).toLocaleDateString() : '—'}
                      </div>
                    </div>
                    <div>
                      <span className="text-[#666]">Last Used</span>
                      <div className="text-[#A0A0A0] font-mono mt-0.5">
                        {details.mcp_token.last_used_at ? new Date(details.mcp_token.last_used_at).toLocaleDateString() : 'Never'}
                      </div>
                    </div>
                    <div>
                      <span className="text-[#666]">Use Count</span>
                      <div className="text-[#A0A0A0] font-mono mt-0.5">{details.mcp_token.use_count || 0}</div>
                    </div>
                    <div>
                      <span className="text-[#666]">Expires</span>
                      <div className="text-[#A0A0A0] font-mono mt-0.5">
                        {details.mcp_token.expires_at ? new Date(details.mcp_token.expires_at).toLocaleDateString() : '—'}
                      </div>
                    </div>
                  </div>

                  {canAdminister && (
                    <div className="flex gap-2 pt-1 border-t border-[#2A2A2A]">
                      {confirmAction === 'mcp_revoke' ? (
                        <div className="flex items-center gap-1.5">
                          <AlertTriangle className="w-3 h-3 text-[#A61E17]" />
                          <span className="text-[10px] text-[#A61E17] font-mono">Revoke MCP token?</span>
                          <button onClick={handleRevokeMcpToken} className={btnDanger + ' !text-[10px]'}>Yes</button>
                          <button onClick={() => setConfirmAction(null)} className={btnSecondary + ' !text-[10px]'}>No</button>
                        </div>
                      ) : confirmAction === 'mcp_regenerate' ? (
                        <div className="flex items-center gap-1.5">
                          <AlertTriangle className="w-3 h-3 text-[#F2A900]" />
                          <span className="text-[10px] text-[#F2A900] font-mono">Regenerate? Old token revoked.</span>
                          <button onClick={handleRegenerateMcpToken} className={btnPrimary + ' !text-[10px]'}>Yes</button>
                          <button onClick={() => setConfirmAction(null)} className={btnSecondary + ' !text-[10px]'}>No</button>
                        </div>
                      ) : (
                        <>
                          <button onClick={() => setConfirmAction('mcp_regenerate')} disabled={actionLoading === 'mcp_regenerate'} className={btnSecondary}>
                            {actionLoading === 'mcp_regenerate' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RefreshCw className="w-3 h-3 inline mr-1" />Regenerate</>}
                          </button>
                          <button onClick={() => setConfirmAction('mcp_revoke')} disabled={actionLoading === 'mcp_revoke'} className={btnDanger}>
                            {actionLoading === 'mcp_revoke' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Trash2 className="w-3 h-3 inline mr-1" />Revoke</>}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-[10px] text-[#666] font-mono">
                    No MCP token generated for this user.
                  </p>
                  {canAdminister && (
                    <button
                      onClick={handleGenerateMcpToken}
                      disabled={actionLoading === 'mcp_generate'}
                      className={btnPrimary}
                    >
                      {actionLoading === 'mcp_generate' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Plus className="w-3 h-3 inline mr-1" />Generate Token</>}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Available Models */}
            {details.available_models && details.available_models.length > 0 && (
              <div className={card}>
                <div className="flex items-center gap-2 mb-2">
                  <Cpu className="w-3.5 h-3.5 text-[#00D4FF]" />
                  <span className="text-xs font-mono text-[#F5F1E8]">Available Models ({details.available_models.length})</span>
                </div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {details.available_models.map(m => (
                    <div key={m.model_id} className="text-[10px] font-mono text-[#A0A0A0] py-0.5">
                      {m.model_name || m.model_id}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Activity */}
            <div className={card}>
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-3.5 h-3.5 text-[#FF4D00]" />
                <span className="text-xs font-mono text-[#F5F1E8]">Activity</span>
                <span className="text-[9px] text-[#666] font-mono ml-auto">
                  {details.activity.total_frays} total frays
                </span>
              </div>
              {details.activity.last_fray && (
                <div className="text-[10px] text-[#A0A0A0] font-mono mb-2">
                  Last fray: {new Date(details.activity.last_fray).toLocaleDateString()}
                </div>
              )}
              {details.activity.recent_frays && details.activity.recent_frays.length > 0 ? (
                <div className="max-h-40 overflow-y-auto space-y-1.5">
                  {details.activity.recent_frays.map(f => (
                    <div key={f.id} className="flex items-center gap-2 text-[10px] font-mono py-1 border-b border-[#1E1E1E] last:border-0">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                        f.status === 'completed' ? 'bg-[#22C55E]' : f.status === 'running' ? 'bg-[#F2A900]' : 'bg-[#666]'
                      }`} />
                      <span className="text-[#A0A0A0] truncate flex-1">{f.template_name || f.scenario || 'Unnamed fray'}</span>
                      <span className="text-[#666] shrink-0">{new Date(f.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[10px] text-[#666] font-mono">No frays yet</p>
              )}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.2s ease-out;
        }
      `}</style>
    </>
  );
}
